import { brokerFilter, type BrokerFilterOption } from '@/mocks/brokers';

type BrokerFilterBarProps = {
  platform: string;
  cost: string;
  onPlatform: (value: string) => void;
  onCost: (value: string) => void;
  onReset: () => void;
  visibleCount: number;
  totalCount: number;
};

type ChipGroupProps = {
  title: string;
  icon: string;
  options: BrokerFilterOption[];
  value: string;
  onChange: (value: string) => void;
};

function ChipGroup({ title, icon, options, value, onChange }: ChipGroupProps) {
  return (
    <div className="flex flex-col gap-2">
      <span className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.1em] text-foreground-500">
        <i className={`${icon} text-xs text-primary-600`} />
        {title}
      </span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => {
          const isActive = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              aria-pressed={isActive}
              className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                isActive
                  ? 'border-primary-500 bg-primary-500 text-background-50'
                  : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function BrokerFilterBar({
  platform,
  cost,
  onPlatform,
  onCost,
  onReset,
  visibleCount,
  totalCount,
}: BrokerFilterBarProps) {
  const isFiltered = platform !== 'all' || cost !== 'all';

  return (
    <div className="mt-6 rounded-xl border border-background-300 bg-background-50 p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-equalizer-2-line text-sm" />
          </span>
          {brokerFilter.title}
        </h3>
        <div className="flex items-center gap-3">
          <span className="font-heading text-[11px] tabular-nums text-foreground-600">
            {brokerFilter.resultPrefix}{' '}
            <span className="font-semibold text-primary-700">{visibleCount}</span>{' '}
            {brokerFilter.resultMiddle} {totalCount} {brokerFilter.resultSuffix}
          </span>
          {isFiltered && (
            <button
              type="button"
              onClick={onReset}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-background-300 px-2.5 py-1 text-[11px] font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-refresh-line text-xs" />
              {brokerFilter.resetLabel}
            </button>
          )}
        </div>
      </div>

      <p className="mt-2 text-[11px] leading-relaxed text-foreground-500">{brokerFilter.hint}</p>

      <div className="mt-4 grid grid-cols-1 gap-4 border-t border-background-200 pt-4 md:grid-cols-2 md:gap-6">
        <ChipGroup
          title={brokerFilter.groupTitles.platform}
          icon="ri-computer-line"
          options={brokerFilter.platformOptions}
          value={platform}
          onChange={onPlatform}
        />
        <div className="md:border-l md:border-background-200 md:pl-6">
          <ChipGroup
            title={brokerFilter.groupTitles.cost}
            icon="ri-money-dollar-circle-line"
            options={brokerFilter.costOptions}
            value={cost}
            onChange={onCost}
          />
        </div>
      </div>

      {cost !== 'all' && (
        <p className="mt-4 flex items-start gap-2 rounded-lg border border-accent-200 bg-accent-50/70 px-3 py-2.5 text-[11px] leading-relaxed text-foreground-700">
          <i className="ri-lightbulb-line mt-0.5 text-sm text-accent-600" />
          {brokerFilter.costHint}
        </p>
      )}
    </div>
  );
}